import React, { useMemo } from 'react';
import isEqualWith from 'lodash/isEqualWith';
import { useSelector, useDispatch } from 'react-redux';
import {
  DuperbrowseStreamContext,
  DuperbrowseStreamContextProps,
} from './DuperbrowseStreamContext';
import { getLastViewedItem } from '../redux/selectors';
import { loadDuperbrowse } from '../redux/actionCreators';
import { useDeepEqualCustomMemo } from '../../utils/hooks/useDeepEqualMemo';
import { DuperbrowseSimpleStream } from '../redux/types';
import { getDeviationEntityId } from '../../redux/normalizr/schemas/deviation';

const DuperbrowseStreamContextProvider: React.FC<
  Omit<DuperbrowseStreamContextProps, 'lastViewedItem' | 'loadDuperbrowse'>
> = ({ children, stream }) => {
  const lastViewedItem = useSelector(getLastViewedItem);
  const dispatch = useDispatch();
  const memodStream = useDeepEqualCustomMemo(stream, (a, b) => {
    // use default comparison for loadable and normalized streams
    if (
      !(a as DuperbrowseSimpleStream)?.items?.[0]?.hasOwnProperty(
        'deviationId'
      ) ||
      !(b as DuperbrowseSimpleStream)?.items?.[0]?.hasOwnProperty('deviationId')
    ) {
      return undefined;
    }

    const { items: aItems, ...aWithoutItems } = a as DuperbrowseSimpleStream;
    const { items: bItems, ...bWithoutItems } = b as DuperbrowseSimpleStream;

    if (!isEqualWith(aWithoutItems, bWithoutItems)) {
      return false;
    }

    return isEqualWith(
      aItems,
      bItems,
      (aItem, bItem) =>
        getDeviationEntityId(aItem) === getDeviationEntityId(bItem)
    );
  });

  const contextProps = useMemo<DuperbrowseStreamContextProps>(
    () => ({
      stream: memodStream,
      lastViewedItem,
      loadDuperbrowse: (itemId, streamId) =>
        dispatch(loadDuperbrowse(itemId, streamId)),
    }),
    [dispatch, lastViewedItem, memodStream]
  );

  return (
    <DuperbrowseStreamContext.Provider value={contextProps}>
      {children}
    </DuperbrowseStreamContext.Provider>
  );
};
DuperbrowseStreamContextProvider.displayName =
  'DuperbrowseStreamContextProvider';

export default DuperbrowseStreamContextProvider;
